import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {DeleteAccountConfirmationDialog} from '../../../../libs/contacts-manager/src/lib/ui/web/dialogs/deleteAccountConfirmationDialog';
import {AsyncHandlerOf, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {buildAccountPublicLink} from '../../../helpers/accountHelpers';
import {useClipboard} from '../../../helpers/hooks/clipboardHookHelpers';
import {useMeasureElement} from '../../../helpers/hooks/domHookHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {AccountDetailsPanelId} from '../../../store/panels/panelStateBase';
import {DropdownItemIcon} from '../../common/dropdowns/dropdownItemIcon';
import {SidePanelHeader} from '../../inboxes/side/sidePanelHeader';
import {
  AccountDetailsTrackEventHandler,
  buildAccountDetailsCopyLinkPayload,
  buildAccountDetailsDeletePayload,
} from '../tracking/accountDetailsTrackingHelpers';
import {useAccountDetailsContext} from './accountDetailsContext';

/*
 * Constants.
 */

const moreDropdownWidth = 200;

/*
 * Style.
 */

const StyledIconButton = styled(IconButton)`
  margin-right: 4px;
`;

/*
 * Props.
 */

interface AccountDetailsPluginHeaderProps {
  panelId: AccountDetailsPanelId;
  account: AccountModel | undefined;
  host: string;
  isInPopup: boolean;
  closeAccountDetails: HandlerOf<AccountDetailsPanelId>;
  openAccountDetailsPopUp: HandlerOf<number>;
  deleteAccountAndCloseAccountDetails: (panelId: AccountDetailsPanelId, accountId: number) => void;
}

/*
 * Component.
 */

export const AccountDetailsPluginHeader: FC<AccountDetailsPluginHeaderProps> = (props) => {
  const theme = useUserTheme();
  const {
    panelId,
    account,
    host,
    isInPopup,
    closeAccountDetails,
    openAccountDetailsPopUp,
    deleteAccountAndCloseAccountDetails,
  } = props;
  const [isShowingDeleteDialog, setIsShowingDeleteDialog] = useState(false);
  const [headerPanelRef, {width: headerPanelWidth}] = useMeasureElement();
  const {copy} = useClipboard();
  const showDeleteDialog = () => setIsShowingDeleteDialog(true);
  const {onTrackEvent} = useAccountDetailsContext();

  const openPopupWindow = () => {
    openAccountDetailsPopUp(headerPanelWidth);
    closeAccountDetails(panelId);
  };

  return (
    <div ref={headerPanelRef}>
      <SidePanelHeader
        title={<Trans id="account-details-header">Account details</Trans>}
        onClose={() => closeAccountDetails(panelId)}
        additionalActionsAfter={
          <>
            {maybeRenderAccountPopover(account, host, copy, showDeleteDialog, onTrackEvent)}
            {!isInPopup && (
              <StyledIconButton
                iconName="popout"
                label={
                  <Trans id="account-details-plugin-header-view-in-external-window">
                    View in external window
                  </Trans>
                }
                onClick={openPopupWindow}
                color={theme.greys.shade70}
                hoverColor={theme.greys.shade80}
              />
            )}
          </>
        }
      />
      {isShowingDeleteDialog && account && (
        <DeleteAccountConfirmationDialog
          account={account}
          onClose={() => setIsShowingDeleteDialog(false)}
          onDelete={() => {
            deleteAccountAndCloseAccountDetails(panelId, account.id);
            onTrackEvent(buildAccountDetailsDeletePayload(account.id));
          }}
        />
      )}
    </div>
  );
};

/*
 * Helpers.
 */

function maybeRenderAccountPopover(
  account: AccountModel | undefined,
  host: string,
  copy: AsyncHandlerOf<string>,
  showDeleteDialog: Handler,
  onTrackEvent: AccountDetailsTrackEventHandler,
) {
  if (!account) {
    return undefined;
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <PopoverCoordinator
          renderButton={(isOpen) => (
            <StyledIconButton
              iconName="moreVertical"
              size={VisualSizesEnum.MEDIUM}
              label={<Trans id="account-details-plugin-header-more">More</Trans>}
              color={theme.greys.shade70}
              hoverColor={theme.greys.shade80}
              hasHover={isOpen}
            />
          )}
          renderModal={(popoverProps) => (
            <Dropdown {...popoverProps} placement="bottom-start" width={moreDropdownWidth}>
              <DropdownList {...popoverProps}>
                <DropdownItem
                  onSelect={() => {
                    copy(buildAccountPublicLink(host, account.id));
                    onTrackEvent(buildAccountDetailsCopyLinkPayload(account.id));
                  }}
                >
                  <DropdownItemIcon iconName="linkSmall" iconSize={16} label={renderCopyAccountLinkText()} />
                  <DropdownItemContent>{renderCopyAccountLinkText()}</DropdownItemContent>
                </DropdownItem>
                <DropdownItem onSelect={showDeleteDialog}>
                  <DropdownItemIcon iconName="trashSmall" iconSize={16} label={renderDeleteAccountText()} />
                  <DropdownItemContent>{renderDeleteAccountText()}</DropdownItemContent>
                </DropdownItem>
              </DropdownList>
            </Dropdown>
          )}
        />
      )}
    </UserThemeConsumer>
  );
}

function renderCopyAccountLinkText() {
  return <Trans id="account-details-plugin-header-copy-account-link">Copy account link</Trans>;
}

function renderDeleteAccountText() {
  return <Trans id="account-details-plugin-header-delete-account">Delete account</Trans>;
}
